<template>
  <div :class="b()">
    <template v-for="widget in templateWidgetList">
      <component
        :is="widget.configComponent"
        v-if="widget.configComponent"
        :id="widget.id"
        :key="widget.componentKey"
        ref="hideConfigComponents"
        :default-configs="widget.defaultConfigs"
        :widget="widget"
        :class="b('hidn-component')"
        @initSuccess="initSuccess.call(this, widget)"
        @initFailed="initFailed(widget)"
      ></component>
    </template>
  </div>
</template>

<script>
import theme from '@zk/theme/lib/variables.scss';
import TodoList from '@/components/GridDashboard/components/TodoList';
import { pick, keyBy } from 'lodash';
// import { getKanbanConfig } from '../util';
import { getKanbanConfig } from 'common-module/src/components/Kanban/utils/getKanbanConfig';
import { mapState } from 'vuex';
import { getWidgets } from '@/components/GridDashboard/util';
// 组件名称对应的处理方式
const GET_SETTING_TYPE = {
  BugWidget: 'configComponent', // √
  BurndownWidget: 'configComponent', // √ *
  CumulativeFlowDiagram: 'configComponent', // √ *
  AssignedWidget: 'configComponent', // √
  ChartWidget: 'configComponent',
  CommentWidget: 'configComponent', // √ 不需要初始化, defaultConfigs 为空
  StatisticsWidget: 'configComponent', // √
  KanBanWidget: 'getKanbanConfig', // √
  /*
  * 返回的 ProjectList 不在 TodoListWidgetConfig 中初始化;
  * 在渲染 TodoList.vue 组件时才会调用;
  * 所以 defaultConfigs 用不上
  * */
  TodoListWidget: 'todoComponent',
  BurnupWidget: 'configComponent'
};

// 添加仪表盘
const addPanel = {
  props: {
    // 执行新增
    doAdd: {
      type: Function,
      required: true
    }
  },
  data() {
    // 初始化 configComponent 的 resolve
    this.initConfigComponentResolve = null;
    this.initConfigComponentReject = null;
    this.isFail = false;
    return {
      // 是否添加仪表盘中
      isAddingPanel: false,
      // 使用模板的组件列表
      templateWidgetList: [],
      widgetComponents: []
    };
  },
  watch: {
    show: {
      handler(newValue) {
        // 初始化状态
        if (newValue) {
          this.isFail = false;
          this.isAddingPanel = false;
          this.templateWidgetList = [];
        }
      }
    },
    templateWidgetList: {
      deep: true,
      handler(newValue) {
        const unLoadStateList = newValue.filter(widget => !widget.loadState);
        // 有没有没加载完成的 && 后续的 watch 变化是因为设置数据
        if (unLoadStateList.length === 0 && this.isFinishGetData === false) {
          this.isFinishGetData = true;
          // 获取 configComponent 实例列表
          const configComponents = this.$refs?.hideConfigComponents || [];
          // 统一调用 getSettings 方法, 获取 setting 数据
          Promise.all(configComponents.map(widget => widget.getSettings())).then(datas => {
            // 遍历 configComponent 实例列表;
            configComponents.forEach((widget, index) => {
              // 获取渲染的 id 值
              const id = widget?.$attrs?.id;
              // 当前 id 所在的索引
              const renderTemplateIndex = this.templateWidgetList.findIndex(widget => widget.id === id);
              // 索引不为 -1
              if (renderTemplateIndex !== -1) {
                // 将对应的值赋值给 loadData
                this.templateWidgetList[renderTemplateIndex].loadData = datas[index];
              }
            });
            // 将初始化组件的resolve进行调用
            this.initConfigComponentResolve?.();
          }).finally(() => {
            // 重置状态
            this.isFinishGetData = false;
          });
        }
      }
    }
  },
  computed: {
    ...mapState('app', ['projectList']),
    // name 对应的 widget
    nameToWidget() {
      return Object.freeze(keyBy(this.widgetComponents, 'name'));
    }
  },
  async beforeCreate() {
    this.widgetComponents = Object.freeze(await getWidgets());
  },
  created(){
    this.eventBus.$on('dashboard:created', this.useTemplateAddPanel);
  },
  beforeDestroy(){
    this.eventBus.$off('dashboard:created', this.useTemplateAddPanel);
  },
  methods: {
    // configComponent 调用的回调
    initSuccess({ id } = {}) {
      const index = this.templateWidgetList.findIndex(widget => widget.id === id);
      if (index !== -1) {
        this.templateWidgetList[index].loadState = true;
      }
    },
    // configComponent 调用的回调
    initFailed() {
      if (!this.isFail) {
        this.isFail = true;
        this.$message.error(this.$t('saas.createPanel.selectTemplate.initFailed'));
        this.initConfigComponentReject?.();
      }
    },
    // 格式化 templateWidgetList 并且等待初始化完成
    formatterTemplateWidgetListAndWaitInit(template) {
      return new Promise((resolve, reject) => {
        this.templateWidgetList = template?.widgets?.map((item, index) => {
          const { type, defaultConfigs } = item;
          const widget = this.nameToWidget[type];
          const result = {
            // 默认配置
            componentKey: `${+new Date()}-${item.id}`,
            ...item,
            // 初始化加载状态
            loadState: false,
            // 初始化值
            loadData: null
          };
          // 有配置组建的
          if (widget && GET_SETTING_TYPE[type] === 'configComponent') {
            result.configComponent = widget?.widget?.configComponent;
          }
          // 代办列表, 目前手动写死 TodoList
          if (GET_SETTING_TYPE[type] === 'todoComponent') {
            result.configComponent = TodoList?.widget?.configComponent;
          }
          // 看板配置
          if (GET_SETTING_TYPE[type] === 'getKanbanConfig') {
            const project = {
              projectId: this.$store.getters['app/getFirstProject'].id,
              title: this.$t('home.widget.kanbanWidget.name')
            };
            getKanbanConfig(this.projectList, void 0, defaultConfigs).then(data => {
              this.templateWidgetList[index].loadState = true;
              this.templateWidgetList[index].loadData = Object.assign(project, data);
            }).catch(() => {
              this.initConfigComponentReject?.();
            });
          }
          return result;
        }) || [];
        this.initConfigComponentResolve = resolve;
        this.initConfigComponentReject = reject;
      });
    },
    formatTemplateWidgetList(){
      return this.templateWidgetList.map((widget) => {
        return Object.assign({}, pick(widget, [
          'abscissa',
          'availableWidgetId',
          'enableDailyPaper',
          'length',
          'ordinate',
          'type',
          'width'
        ]), { settings: JSON.stringify(Object.assign(widget.loadData || {}, widget.recommendSetting || {})) }, { name: widget?.loadData?.title || widget?.templateName });
      });
    },
    // 提交数据
    async submitData(data, templateId, templateName) {
      await this.doAdd(data, templateId, templateName);
    },
    // 使用模板添加仪表盘
    async useTemplateAddPanel(template, justGetData = false) {
      this.isFail = false;
      // 格式化数据并初始化完成
      this.isFinishGetData = false;
      this.templateWidgetList = [];
      await this.formatterTemplateWidgetListAndWaitInit(template);
      const dataRes = this.formatTemplateWidgetList();
      if (justGetData) {
        return dataRes;
      }
      // 提交数据
      await this.submitData(dataRes, template?.id, template?.defaultName);
      // 结束提交, 移除配置组件
      this.templateWidgetList = [];
    }
  }
};

export default {
  name: 'BuildDashboard',
  mixins: [addPanel],
  data() {
    // 是否完成数据的获取
    this.isFinishGetData = false;
    return {
      // bem
      bemNS: theme.bemNS,
      // 是否展示
      show: false
    };
  },
  methods: {

  }
};
</script>

<style lang="scss">
@include b('build-dashboard') {
  @include e(hidn-component) {
    position: fixed;
    z-index: -1;
    top: -9999px;
    left: -9999px;
    opacity: 0;
  }
}
</style>
